var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Table } from "@mui/material";
const sort = (tableData, sortIndex, reverse, propToSort) => {
    var _a;
    const sortedTableRows = sortByMethod(tableData.rows, (_a = tableData.headers[sortIndex]) === null || _a === void 0 ? void 0 : _a.sortingMethod, propToSort);
    return reverse ? sortedTableRows.reverse() : sortedTableRows;
};
const sortByMethod = (tableRows, sortingMethod, propToSort) => {
    let sortedTableRows = [...tableRows];
    if (typeof sortingMethod === "function") {
        return sortedTableRows.sort(sortingMethod);
    }
    switch (sortingMethod) {
        case "alphabetically":
            return sortedTableRows.sort((a, b) => (String(a[propToSort] || "").localeCompare(String(b[propToSort] || ""))));
        case "numeric":
            return sortedTableRows.sort((a, b) => Number(a[propToSort]) - Number(b[propToSort]));
        case "date":
            return sortedTableRows.sort((a, b) => {
                const aValue = String(a[propToSort]);
                const bValue = String(b[propToSort]);
                if (!a[propToSort] || aValue === "") {
                    return 1;
                }
                if (!b[propToSort] || bValue === "") {
                    return -1;
                }
                return new Date(bValue).getTime() - new Date(aValue).getTime();
            });
        default:
            return sortedTableRows.sort((a, b) => a[propToSort] < b[propToSort] ? -1 : 1);
    }
};
export const SortableTable = (props) => {
    const { children, tableData, stickyFirstColumn, renderTableHead, defaultColumnToSort, defaultReverseSort, dataTrackingId, isOverridingDefaultSort } = props, tableProps = __rest(props, ["children", "tableData", "stickyFirstColumn", "renderTableHead", "defaultColumnToSort", "defaultReverseSort", "dataTrackingId", "isOverridingDefaultSort"]);
    const [isIndexReversedSortArray, setIsIndexReversedSortArray] = useState(new Array(tableData.headers.length).fill(undefined));
    const [isHorizontalScrolled, setIsHorizontalScrolled] = useState(false);
    const [tableRows, setTableRows] = useState(tableData.rows);
    const [tableRef, setTableRef] = useState();
    const tableRefWrapper = useCallback((node) => {
        setTableRef({ current: node });
    }, []);
    useEffect(() => {
        var _a;
        (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.parentElement.addEventListener("scroll", (e) => setIsHorizontalScrolled(e.currentTarget["scrollLeft"] > 0));
    }, [tableRef]);
    useLayoutEffect(() => {
        reloadTableState([...isIndexReversedSortArray]);
    }, []);
    useLayoutEffect(() => {
        reloadTableState([...isIndexReversedSortArray]);
    }, [tableData.rows]);
    useLayoutEffect(() => {
        reloadTableState(new Array(tableData.headers.length).fill(undefined));
    }, [tableData.headers]);
    let sxProps = Object.assign(Object.assign({}, props.sx), { position: "relative" });
    if (stickyFirstColumn) {
        sxProps = Object.assign(Object.assign({}, sxProps), { position: "relative", "& tr": {
                "& th:first-child, & td:first-child": {
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#FFF",
                    zIndex: 3,
                    "&::before": {
                        content: "''",
                        boxShadow: "-5px 0 9px 6px rgb(0,0,0,0.25)",
                        clipPath: "inset(0px -15px 0px 0px)",
                        width: 0,
                        height: "100%",
                        position: "absolute",
                        left: "100%",
                        top: 0,
                        zIndex: 2,
                        transition: "opacity .3s ease-in-out",
                        opacity: isHorizontalScrolled ? 1 : 0
                    }
                }
            }, "& tbody tr:hover": {
                "& th:first-child, & td:first-child": {
                    backgroundColor: "#f5f8fe"
                }
            } });
    }
    if (tableProps.stickyHeader && stickyFirstColumn) {
        sxProps = Object.assign(Object.assign({}, sxProps), { "& thead": {
                "& th": {
                    zIndex: 4,
                    "&:first-child": {
                        top: 0,
                        zIndex: 5
                    }
                }
            } });
    }
    const reloadTableState = (isIndexReversedSortArray) => {
        var _a;
        const index = isIndexReversedSortArray.findIndex(value => value !== undefined) >= 0 ?
            isIndexReversedSortArray.findIndex(value => value !== undefined) :
            defaultColumnToSort < tableData.headers.length ? defaultColumnToSort : 0;
        const propToSort = (_a = tableData.headers[index]) === null || _a === void 0 ? void 0 : _a.propName;
        let tempIsIndexReversedSortArray = isIndexReversedSortArray;
        tempIsIndexReversedSortArray[index] = tempIsIndexReversedSortArray[index] !== undefined ? tempIsIndexReversedSortArray[index] : !!defaultReverseSort;
        setIsIndexReversedSortArray(tempIsIndexReversedSortArray);
        setTableRows(isOverridingDefaultSort ? tableData.rows : sort(tableData, index, tempIsIndexReversedSortArray[index], propToSort));
    };
    const onSort = (header) => {
        const sortIndex = tableData.headers.findIndex(value => value.name === header.name);
        const propToSort = tableData.headers[sortIndex].propName;
        let tempIsIndexReversedSortArray = new Array(tableData.headers.length).fill(undefined);
        tempIsIndexReversedSortArray[sortIndex] = isIndexReversedSortArray[sortIndex] === undefined ? false : !isIndexReversedSortArray[sortIndex];
        setIsIndexReversedSortArray(tempIsIndexReversedSortArray);
        setTableRows(isOverridingDefaultSort ? tableData.rows : sort(tableData, sortIndex, tempIsIndexReversedSortArray[sortIndex], propToSort));
    };
    const headers = tableData.headers.map((header, index) => ({ name: header.name, propName: header.propName, isReversedSort: isIndexReversedSortArray[index] }));
    const tableHead = renderTableHead({ sort: onSort, headers });
    return (_jsxs(Table, Object.assign({}, tableProps, { sx: sxProps, "data-tracking-id": dataTrackingId, ref: tableRefWrapper, children: [tableHead, typeof children === "function" ? children(tableRows) : children] })));
};
export default SortableTable;
//# sourceMappingURL=SortableTable.js.map